import React, { useEffect, useState, ReactNode, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import { Button } from "@mui/material";
import { styled } from "@mui/system";
import { isEmpty, isNumber, round } from "lodash";
import { init } from "echarts";

import Color from "@tool/color";
import moDemand from "../../../store";
import moIndustry from "../../../store/industry";
import moIndex from "@/page/index/store";

interface IProps {}
interface IState {}
let chart: any = null;

const IndustryTrend = (props: IProps, state: IState) => {
  const navi = useNavigate();
  const industryRef = useRef<any>(null);
  const { isMock } = moIndex;
  const { trendYear, trendMonth, trendQuarter, trendActive } = moIndustry;

  const getData = useMemo(() => {
    const xData: any = [];
    const num: any = [];
    const salary: any = [];
    if (trendActive === "year" && !isEmpty(trendYear)) {
      trendYear.data.forEach((res: any) => {
        xData.push(res.year);
        num.push(res.amount);
        salary.push(res.salary);
      });
    }
    if (trendActive === "quarter" && !isEmpty(trendQuarter)) {
      trendQuarter.forEach((res: any) => {
        xData.push(res.time_str);
        num.push(res.amount);
        salary.push(res.salary);
      });
    }
    if (trendActive === "month" && !isEmpty(trendMonth)) {
      trendMonth.forEach((res: any) => {
        xData.push(res.time_str);
        num.push(res.amount);
        salary.push(res.salary);
      });
    }
    return { xData, num, salary };
  }, [trendYear, trendMonth, trendQuarter, trendActive]);

  const getWidth = useMemo(() => {
    const id: any = document.getElementById("contents");
    let show: boolean = false;
    let num: number = 100;
    if (trendActive === "month") {
      if (trendMonth?.length * 120 > id?.clientWidth - 410) {
        show = true;
        num = Math.floor(
          ((id.clientWidth - 410) / 120 / trendMonth.length) * 100
        );
      } else {
        show = false;
        num = 100;
      }
    }

    return { show, num };
  }, [trendMonth, trendActive]);

  const colorList = [Color.primary, Color.secondary];
  const render = () => {
    if (chart) chart.dispose();
    chart = init(industryRef.current);
    const option = {
      color: colorList,
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
          label: { show: true },
        },
        show: true,
        formatter: (parmes: any) => {
          let htl = `<p  style="font-family: 'Roboto';font-size:14px;font-weight:500;color:${Color.blackColor(
            1
          )}">${parmes[0].name}</p>`;
          parmes?.forEach((element: any, i: number) => {
            htl += `<p style="margin-top:10px;display:flex;
                         justify-content: flex-start;align-items: center;">
                      <span
                        style="display:inline-block;
                        width:10px;height:10px;background:${
                          colorList[element.seriesIndex]
                        };
                        border-radius:50%;margin-right:5px">
                      </span>
                      <span style="font-family: 'Roboto';font-size:12px;">${
                        element.seriesName
                      }</span>
                      <span style="margin:0 15px"></span>
                      <span style="font-family: 'Roboto';font-size:14px;flex:1;text-align:right;color:${Color.blackColor(
                        1
                      )}">
                        ${
                          isNumber(element.value)
                            ? element.value.toLocaleString()
                            : "-"
                        }
                      </span>
                   </p>`;
          });
          return htl;
        },
      },
      legend: {
        data: ["招聘量", "招聘薪酬"],
        bottom: 20,
        icon: "roundRect",
      },
      grid: {
        left: 30,
        right: 10,
        bottom: 60,
        top: 10,
        containLabel: true,
      },
      dataZoom: [
        {
          type: "inside",
          show: getWidth.show,
          zoomLock: true,
          // xAxisIndex: [0],
          end: getWidth.num, // 30%
          start: 0, // 0
          height: 20,
          bottom: 10,
          fillerColor: Color.white,
          borderRadius: 10,
          brushSelect: false,
          radiusAxisIndex: 10,
          handleIcon: "",
        },
        {
          show: getWidth.show,
          zoomLock: true,
          end: getWidth.num,
          start: 0,
          type: "slider",
          xAxisIndex: [0],
          handleSize: 0,
          height: 10,
          bottom: 10,
          borderColor: Color.white,
          fillerColor: "rgba(50, 50, 50, 0.1)",
          borderRadius: 0,
          backgroundColor: "rgba(50, 50, 50, 0.04)",
          showDataShadow: false,
          showDelay: false,
          realtime: true,
          filterMode: "filter",
          brushSelect: false,
          showDetail: false,
          handleIcon: "",
        },
      ],
      xAxis: {
        type: "category",
        data: getData.xData,
        axisTick: { show: false },
        axisLine: {
          lineStyle: { color: Color.ccc },
        },
        axisLabel: {
          color: Color.blackColor(0.85),
          fontFamily: "Roboto",
        },
      },
      yAxis: [
        {
          type: "value",
          axisLine: {
            show: false,
            lineStyle: { color: Color.ccc },
          },
          axisTick: { show: false },
          axisLabel: {
            show: true,
            fontSize: 12,
            color: Color.blackColor(0.5),
            fontFamily: "Roboto",
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: Color.blackColor(0.08),
              width: 1,
              type: "dashed",
            },
          },
        },
        {
          type: "value",
          axisLine: {
            show: false,
            lineStyle: { color: Color.ccc },
          },
          axisTick: { show: false },
          axisLabel: {
            formatter: (val: number) => {
              if (val === 0) return 0;
              return `${Number(val).toLocaleString()}`;
            },
            show: true,
            fontSize: 12,
            color: Color.blackColor(0.5),
            fontFamily: "Roboto",
          },
          splitLine: {
            show: false,
            lineStyle: {
              color: Color.blackColor(0.08),
              width: 1,
              type: "dashed",
            },
          },
        },
      ],
      series: [
        {
          name: "招聘量",
          type: "line",
          data: getData.num,
        },
        {
          name: "招聘薪酬",
          type: "line",
          data: getData.salary,
          yAxisIndex: 1,
        },
      ],
    };
    chart.setOption(option, true);
  };

  useEffect(() => {
    render();
    window.addEventListener("resize", render);
    return () => {
      window.removeEventListener("resize", render);
    };
  }, [trendActive, trendMonth, trendQuarter, trendYear]);

  return (
    <section
      className="industry-trend-warp"
      ref={industryRef}
      style={{
        backgroundImage: isMock
          ? "url(https://file.smartsalary.cn/web-img%402.0/market/image/ismock.png)"
          : "",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center center",
      }}
    />
  );
};

export default observer(IndustryTrend);
